import { makeAutoObservable } from "mobx";
import { corpCompanyService } from "@/services/corp/CorpCompanyService";
import { recheckPrincipal } from "@/services/corp/corpGuard";
import { corpOrdersService } from "@/services/corp/CorpOrdersService";
import { corpReportService } from "@/services/corp/CorpReportService";
import { corpSettlementsService } from "@/services/corp/CorpSettlementsService";
import { CompanyLinksStore } from "@/stores/CompanyLinksStore";
import { CompanyReportStore } from "@/stores/CompanyReportStore";
import { CorpCompanyStore } from "@/stores/corp/CorpCompanyStore";
import { CorpOrdersStore } from "@/stores/corp/CorpOrdersStore";
import { CorpSettlementsStore } from "@/stores/corp/CorpSettlementsStore";
/**
 * Выбор компании (CCR-037) переживает перезагрузку страницы в пределах сессии браузера — ключ привязан к adminId,
 * чтобы выбор одного админа не достался другому. Недоступное хранилище (приватный режим) игнорируется.
 */
const ACTIVE_COMPANY_KEY = "corp.activeCompanyId";
const readRememberedCompany = (adminId) => {
    if (!adminId)
        return null;
    try {
        const raw = window.sessionStorage.getItem(ACTIVE_COMPANY_KEY);
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        return parsed.adminId === adminId && typeof parsed.companyId === "string" ? parsed.companyId : null;
    }
    catch {
        return null;
    }
};
const rememberCompany = (adminId, companyId) => {
    if (!adminId)
        return;
    try {
        window.sessionStorage.setItem(ACTIVE_COMPANY_KEY, JSON.stringify({ adminId, companyId }));
    }
    catch {
        // хранилище недоступно — выбор живёт только в сторе
    }
};
export class CorpRootStore {
    constructor() {
        this.session = null;
        this.activeCompanyId = null;
        this.setSession = (session) => {
            this.session = session;
            const remembered = readRememberedCompany(session?.adminId ?? null);
            const rememberedIsValid = session?.companies.some((company) => company.id === remembered) ?? false;
            this.activeCompanyId = (rememberedIsValid ? remembered : null) ?? session?.companies[0]?.id ?? null;
        };
        this.setActiveCompany = (companyId) => {
            if (companyId === this.activeCompanyId)
                return;
            if (!this.session?.companies.some((company) => company.id === companyId))
                return;
            this.activeCompanyId = companyId;
            rememberCompany(this.session?.adminId ?? null, companyId);
            this.activateCompany(companyId);
        };
        /** Инициализирует дочерние сторы под активную компанию — вызывается из `CorpLayout` при монтировании/смене компании. */
        /**
         * Смена активной компании (UC1130 BR-3): дочерние сторы разделов сбрасываются, сводка перечитывается;
         * каждый раздел заново инициализирует свой стор по изменению `activeCompanyId` (`init(activeCompanyId)`).
         * При монтировании кабинета вызывается только `companyStore.load` (CorpLayout) — на входе запрашиваются
         * лишь `corp_my_companies` / `corp_my_tariff` (BR-UI-05).
         */
        this.activateCompany = (companyId) => {
            this.linksStore.dispose();
            this.ordersStore.dispose();
            this.settlementsStore.dispose();
            this.reportStore.dispose();
            void this.companyStore.load(companyId);
        };
        /**
         * Смена раздела (WP-DSP-3-FIX, критерий 2): перечитать `corp_whoami` (отзыв доступа → DeniedPage через
         * `resolvePrincipal`) и сводку компании. Без polling (BR-UI-05).
         */
        this.recheckAccess = async () => {
            const companyId = this.activeCompanyId;
            await recheckPrincipal();
            if (companyId && this.activeCompanyId === companyId)
                await this.companyStore.load(companyId);
        };
        /** `corp-cabinet-refresh`: сводка + только уже инициализированные разделы. */
        this.refreshAll = async () => {
            const companyId = this.activeCompanyId;
            if (!companyId)
                return;
            await Promise.all([
                this.companyStore.load(companyId),
                this.linksStore.load(),
                this.ordersStore.load(),
                this.settlementsStore.load(),
                this.reportStore.load(),
            ]);
        };
        this.reset = () => {
            this.session = null;
            this.activeCompanyId = null;
            this.companyStore.dispose();
            this.linksStore.dispose();
            this.ordersStore.dispose();
            this.settlementsStore.dispose();
            this.reportStore.dispose();
        };
        this.companyStore = new CorpCompanyStore(corpCompanyService);
        this.linksStore = new CompanyLinksStore();
        this.ordersStore = new CorpOrdersStore(corpOrdersService);
        this.settlementsStore = new CorpSettlementsStore(corpSettlementsService);
        this.reportStore = new CompanyReportStore(corpReportService);
        makeAutoObservable(this);
    }
    // Активная компания сессии — по умолчанию первая по имени (`corp_whoami` уже сортирует, решение D2).
    get company() {
        if (!this.session)
            return null;
        return this.session.companies.find((company) => company.id === this.activeCompanyId) ?? this.session.companies[0] ?? null;
    }
}
let corpRootStoreInstance = null;
export const getCorpRootStore = () => {
    if (!corpRootStoreInstance) {
        corpRootStoreInstance = new CorpRootStore();
    }
    return corpRootStoreInstance;
};
export const resetCorpRootStore = () => {
    corpRootStoreInstance?.reset();
};
